'use client';

/**
 * Schoolhouse summary card for the browse grid.
 *
 * Module: components/schoolhouse/SchoolhouseCard
 * Layer:  component
 *
 * Purpose:
 *     Renders one {@link SchoolhouseSummary} as a linked poster card showing
 *     the cover image, name, teacher byline, classroom/student counts, rating,
 *     and a "View" affordance. Mirrors the courses CourseCard structure so the
 *     two grids read consistently and every unit clears the CC<=5 / <=60-line
 *     gates.
 *
 * AI context:
 *     - Entry points called by: src/app/schoolhouse/page.tsx (browse grid).
 *     - Depends on: next/image, next/link, lib/schoolhouse types, lib/i18n.
 *     - Side effects: none (pure presentational; navigation via <Link>).
 *     - Invariants: every visible string flows through `t()`; the slug is an
 *       app-owned URL-safe segment (cast documented at the href builder).
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - `cover_image_url` is backend-controlled; next/image only loads it from
 *       the https remotePatterns allow-list and React escapes all text output.
 */

import type { Route } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from '@/lib/i18n';
import type { SchoolhouseSummary } from '@/lib/schoolhouse';

/** Decorative, non-translatable cover placeholder glyph. */
const COVER_GLYPH = '✎' as const;

/** Build the typed detail route for a schoolhouse slug. */
function detailHref(slug: string): Route {
  // WHY: typedRoutes types this template as Route at the call site; the cast
  // documents that the slug is an app-owned, URL-safe path segment.
  return `/schoolhouse/${slug}` as Route;
}

/**
 * Cover image with a building-glyph placeholder fallback.
 *
 * @remarks Uses next/image (no-img-element); external https hosts are
 *   permitted via next.config images.remotePatterns.
 */
function Cover(props: { readonly url: string | null; readonly alt: string }): React.JSX.Element {
  if (props.url) {
    return <Image src={props.url} alt={props.alt} fill sizes='100vw' className='object-cover' />;
  }
  return (
    <div className='flex size-full items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200'>
      <span className='text-4xl text-purple-400' aria-hidden='true'>
        {COVER_GLYPH}
      </span>
    </div>
  );
}

/** Rating pill, or a "New" label when no rating has been recorded yet. */
function RatingBadge(props: { readonly rating: number | null }): React.JSX.Element {
  const { t } = useTranslation();
  if (props.rating === null) {
    return <span className='text-sm opacity-70'>{t('schoolhouse.card.noRating')}</span>;
  }
  return <span className='text-sm font-semibold'>{props.rating.toFixed(1)}</span>;
}

/** Classroom + student count footer row. */
function CardStats(props: { readonly school: SchoolhouseSummary }): React.JSX.Element {
  const { t } = useTranslation();
  const { school } = props;
  return (
    <div className='mt-3 flex items-center gap-4 text-sm opacity-70'>
      <span>{t('schoolhouse.card.classroomCount', { count: school.classroom_count })}</span>
      <span>{t('schoolhouse.card.studentCount', { count: school.student_count.toLocaleString() })}</span>
    </div>
  );
}

/** Poster card for a single schoolhouse summary. */
export function SchoolhouseCard(props: {
  readonly school: SchoolhouseSummary;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { school } = props;
  return (
    <Link
      href={detailHref(school.slug)}
      className='group overflow-hidden rounded-xl border bg-white transition-shadow hover:shadow-lg'
    >
      <div className='relative aspect-video bg-gray-100'>
        <Cover url={school.cover_image_url} alt={school.name} />
        {school.is_featured ? (
          <span className='absolute right-3 top-3 rounded bg-yellow-500 px-2 py-1 text-xs font-semibold text-white'>
            {t('schoolhouse.card.featuredBadge')}
          </span>
        ) : null}
      </div>
      <div className='p-4'>
        <h3 className='line-clamp-2 font-semibold transition-colors group-hover:text-purple-600'>
          {school.name}
        </h3>
        <p className='mt-1 text-sm opacity-70'>
          {t('schoolhouse.card.byTeacher', { name: school.teacher_name })}
        </p>
        <CardStats school={school} />
        <div className='mt-3 flex items-center justify-between border-t pt-3'>
          <RatingBadge rating={school.rating} />
          <span className='text-sm font-medium text-purple-600 group-hover:underline'>
            {t('schoolhouse.card.view')}
          </span>
        </div>
      </div>
    </Link>
  );
}
